import React, { FC, useEffect, useRef } from 'react';
import { matchPath, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { get, isEmpty, findLast } from 'lodash';
import { createLink } from '@src/util/createLink';
import { CircularLoading } from '@src/components/CircularLoading';
import {
  FUNNEL_MAIN,
  NEW_FUNNEL_ROUTE,
  FUNNEL_MESSAGE_ROUTE,
  FUNNEL_APPLICATIONS_ROUTE,
  FUNNEL_REVIEW_ROUTE,
  FUNNEL_ROUTE,
  FUNNEL_EDIT_ROUTE,
  FUNNEL_PITCH_ROUTE,
} from '@src/Routes/links';
import {
  OpeningQueryQuery,
  OpeningStateEnum,
  useCompanyByProfileLazyQuery,
  useOpeningQueryLazyQuery,
  useRelocationBenefitsQuery,
  useSessionQuery,
} from '@src/graphql/generated';
import { useAppSelector } from '@src/redux';
import { getCustomerStatus } from '@src/redux/selectors/recruiter';
import CreateOpening from './CreateOpening';
import CreateQuestions from './CreateQuestions';
import { CreateMessage } from './CreateMessage';
import { FunnelHeader } from './FunnelHeader';
import {
  isApplicationEnabled,
  isMessageEnabled,
  isPitchEnabled,
  isReviewEnabled,
  isTargetEnabled,
} from './util/creationSteps';
import { CreatePitch } from './CreatePitch';
import { FunnelCheckout } from './FunnelCheckout';
import { canActivateFreemium } from './util/canActivateFreemium';
import FunnelReview from './FunnelReview';
import './styles.scss';

function getRedirectLink(opening: OpeningQueryQuery['opening']) {
  const enabledLinkMap = [
    { isEnabled: isTargetEnabled, to: FUNNEL_EDIT_ROUTE },
    { isEnabled: isMessageEnabled, to: FUNNEL_MESSAGE_ROUTE },
    { isEnabled: isApplicationEnabled, to: FUNNEL_APPLICATIONS_ROUTE },
    { isEnabled: isPitchEnabled, to: FUNNEL_PITCH_ROUTE },
    { isEnabled: isReviewEnabled, to: FUNNEL_REVIEW_ROUTE },
  ];

  const selectedLink = findLast(enabledLinkMap, ({ isEnabled }) => isEnabled(opening));

  return selectedLink?.to;
}

export const CreateFunnel: FC = () => {
  const isMounted = useRef(false);

  const history = useHistory();
  const location = useLocation();

  const { data: sessionData } = useSessionQuery();

  const recruiter = sessionData?.currentRecruiter;
  const company = sessionData?.company;

  const match = matchPath(location.pathname, {
    exact: false,
    path: FUNNEL_MAIN,
    strict: false,
  });

  const openingId = match?.params?.openingId;

  const [getOpening, { data: openingData, loading: isOpeningLoading }] = useOpeningQueryLazyQuery();

  const [getCompanyProfile, { data: companyData, loading: isCompanyLoading }] = useCompanyByProfileLazyQuery({
    fetchPolicy: 'network-only',
  });

  const { data: relocationBenefitsData, loading: isRelocationBenefitsLoading } = useRelocationBenefitsQuery({
    fetchPolicy: 'network-only',
  });

  useEffect(() => {
    if (company?.profile.id) {
      getCompanyProfile({
        variables: { companyProfileId: company?.profile.id },
      });
    }
  }, [company]);

  useEffect(() => {
    if (openingId && openingId !== 'new') {
      getOpening({
        variables: { openingId },
      });
    }
  }, [openingId]);

  useEffect(() => {
    if (openingData && !isEmpty(openingData)) {
      const opening = get(openingData, 'opening') || {};

      if ([OpeningStateEnum.ACTIVE, OpeningStateEnum.PAUSED].includes(opening.state) && isEmpty(opening)) {
        if (recruiter?.isSiteAdmin) {
          history.replace({
            pathname: createLink({ link: location.pathname }),
          });
        } else {
          history.replace({
            pathname: createLink({ link: FUNNEL_ROUTE, params: { openingId: opening.id } }),
          });
        }
      }

      const redirectLink = getRedirectLink(opening);

      // Redirect correct step.
      if (!isMounted.current) {
        isMounted.current = true;

        history.replace({
          pathname: createLink({ link: redirectLink, params: { openingId: opening.id } }),
        });
      }
    }
  }, [openingData, recruiter]);

  const opening = openingData?.opening;

  const createOpeningComponent = () => {
    return (
      <CreateOpening
        benefits={relocationBenefitsData?.relocationBenefits}
        company={companyData?.company}
        companyProfile={companyData?.company.profile}
        opening={opening || {}}
        openingId={openingId}
        recruiter={recruiter}
      />
    );
  };

  const isLoading = isCompanyLoading || isRelocationBenefitsLoading || isOpeningLoading;

  const { planInfo, planType } = useAppSelector(getCustomerStatus);

  const shouldShowFreemiumCheckout = canActivateFreemium(recruiter, planInfo, planType);

  return (
    <div className="funnel">
      <FunnelHeader opening={opening} />

      {isLoading ? (
        <div className="funnel">
          <CircularLoading className="funnel__loading" />
        </div>
      ) : (
        <Switch>
          <Route exact path={NEW_FUNNEL_ROUTE}>
            {createOpeningComponent()}
          </Route>

          <Route exact path={FUNNEL_EDIT_ROUTE}>
            {createOpeningComponent()}
          </Route>

          {!!opening && (
            <Route exact path={FUNNEL_MESSAGE_ROUTE}>
              <CreateMessage
                benefits={relocationBenefitsData?.relocationBenefits}
                company={companyData?.company}
                isLoading={isOpeningLoading}
                opening={opening}
              />
            </Route>
          )}

          <Route exact path={FUNNEL_APPLICATIONS_ROUTE}>
            <CreateQuestions company={companyData?.company} opening={opening || {}} recruiter={recruiter} />
          </Route>

          {!!opening && (
            <Route exact path={FUNNEL_PITCH_ROUTE}>
              <CreatePitch
                company={companyData?.company}
                isLoading={isOpeningLoading || isCompanyLoading}
                opening={opening}
                recruiter={recruiter}
              />
            </Route>
          )}

          {!!opening && (
            <Route exact path={FUNNEL_REVIEW_ROUTE}>
              {shouldShowFreemiumCheckout ? (
                <FunnelCheckout openingId={opening.id} />
              ) : (
                <FunnelReview
                  benefits={relocationBenefitsData?.relocationBenefits}
                  company={companyData?.company}
                  opening={opening || {}}
                  recruiter={recruiter}
                />
              )}
            </Route>
          )}
        </Switch>
      )}
    </div>
  );
};
